import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
} from "@mui/material";

import Constants from "Helpers/Constants";
import init from "Helpers/WindowToken";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDSnackbar from "components/MDSnackbar";
import PurchaseOfferManager from "./PurchaseOfferManager";

function SubscriptionManagement() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [plans, setPlans] = useState([]);
  const [settings, setSettings] = useState({
    subscription_catalog_visible: false,
    new_subscription_purchases_enabled: false,
    subscription_renewals_enabled: false,
    ai_credit_purchases_enabled: false,
    subscription_test_redemption_enabled: false,
    ai_test_generation_enabled: false,
    refund_requests_enabled: false,
    maintenance_message: "",
    refund_window_days: 7,
  });
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [notification, setNotification] = useState(false);
  const [message, setMessage] = useState("");
  const [notiColor, setNotiColor] = useState("success");

  const showNotice = (content, color = "success") => {
    setMessage(content);
    setNotiColor(color);
    setNotification(true);
    setTimeout(() => {
      setNotification(false);
      setMessage("");
      setNotiColor("success");
    }, 2500);
  };

  const loadData = async () => {
    try {
      if (init() !== "success") {
        window.location.assign("/signin");
        return;
      }

      setLoading(true);
      const [plansRes, settingsRes] = await Promise.all([
        axios.get(Constants.getUrls.getSubscriptionPlans),
        axios.get(Constants.getUrls.getSubscriptionSettings),
      ]);
      setPlans(plansRes?.data?.data || []);
      setSettings((prev) => ({
        ...prev,
        ...(settingsRes?.data?.data || {}),
      }));
    } catch (error) {
      showNotice(error?.response?.data?.message || "Unable to load subscription module.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const recommendedPlan = useMemo(
    () => plans.find((item) => item?.is_recommended),
    [plans]
  );

  const handlePlanToggle = async (planId, nextState) => {
    try {
      if (init() !== "success") {
        window.location.assign("/signin");
        return;
      }

      await axios.patch(`${Constants.putUrls.updateSubscriptionPlan}${planId}`, nextState);
      showNotice("Plan updated successfully.");
      await loadData();
    } catch (error) {
      showNotice(error?.response?.data?.message || "Unable to update plan.", "error");
    }
  };

  const handleDeletePlan = async () => {
    try {
      if (!deleteCandidate) {
        return;
      }

      if (init() !== "success") {
        window.location.assign("/signin");
        return;
      }

      await axios.delete(`${Constants.deleteUrls.deleteSubscriptionPlan}${deleteCandidate._id}`);
      showNotice("Subscription plan deleted successfully.");
      setDeleteCandidate(null);
      await loadData();
    } catch (error) {
      showNotice(error?.response?.data?.message || "Unable to delete plan.", "error");
    }
  };

  const handleSettingsSave = async (event) => {
    event.preventDefault();
    try {
      if (init() !== "success") {
        window.location.assign("/signin");
        return;
      }

      setSavingSettings(true);
      await axios.patch(Constants.putUrls.updateSubscriptionSettings, settings);
      showNotice("Subscription settings updated successfully.");
      await loadData();
    } catch (error) {
      showNotice(error?.response?.data?.message || "Unable to update settings.", "error");
    } finally {
      setSavingSettings(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="success"
                borderRadius="lg"
                coloredShadow="success"
              >
                <MDBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", md: "center" }}
                  flexDirection={{ xs: "column", md: "row" }}
                  gap={2}
                >
                  <MDBox>
                    <MDTypography variant="h6" color="white">
                      Subscription Management
                    </MDTypography>
                    <MDTypography variant="button" color="white" opacity={0.85}>
                      Manage visibility, activation, and pricing packages from a dedicated admin
                      module.
                    </MDTypography>
                    {recommendedPlan ? (
                      <MDBox mt={1}>
                        <MDTypography variant="caption" color="white" fontWeight="bold">
                          Recommended plan right now: {recommendedPlan?.name}
                        </MDTypography>
                      </MDBox>
                    ) : null}
                  </MDBox>

                  <MDButton color="white" variant="outlined" onClick={() => navigate("/subscriptions/create")}>
                    Create Plan
                  </MDButton>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12} lg={5}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant="h6">Feature Flags</MDTypography>
                <MDBox component="form" mt={2} onSubmit={handleSettingsSave}>
                  <Grid container spacing={1}>
                    {[
                      ["subscription_catalog_visible", "Show subscription catalogue"],
                      ["new_subscription_purchases_enabled", "Enable new purchases"],
                      ["subscription_renewals_enabled", "Enable renewals"],
                      ["ai_credit_purchases_enabled", "Enable AI credit purchase"],
                      ["subscription_test_redemption_enabled", "Enable test redemption"],
                      ["ai_test_generation_enabled", "Enable AI test generation"],
                      ["refund_requests_enabled", "Enable refund requests"],
                    ].map(([key, label]) => (
                      <Grid item xs={12} md={6} key={key}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={Boolean(settings[key])}
                              onChange={(event) =>
                                setSettings((prev) => ({
                                  ...prev,
                                  [key]: event.target.checked,
                                }))
                              }
                            />
                          }
                          label={label}
                        />
                      </Grid>
                    ))}

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Refund Window (days)"
                        value={settings.refund_window_days || 7}
                        onChange={(event) =>
                          setSettings((prev) => ({
                            ...prev,
                            refund_window_days: Number(event.target.value || 0),
                          }))
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Maintenance Message"
                        value={settings.maintenance_message || ""}
                        onChange={(event) =>
                          setSettings((prev) => ({
                            ...prev,
                            maintenance_message: event.target.value,
                          }))
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <MDButton type="submit" color="info" disabled={savingSettings}>
                        {savingSettings ? "Saving..." : "Save Feature Settings"}
                      </MDButton>
                    </Grid>
                  </Grid>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12} lg={7}>
            <Card>
              <MDBox p={3}>
                <MDBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  flexDirection={{ xs: "column", sm: "row" }}
                  gap={1.5}
                >
                  <MDTypography variant="h6">Current Plans</MDTypography>
                  <MDButton color="success" onClick={() => navigate("/subscriptions/create")}>
                    Create Plan
                  </MDButton>
                </MDBox>

                <MDBox mt={2}>
                  {loading ? (
                    <MDTypography variant="button" color="text">
                      Loading subscription plans...
                    </MDTypography>
                  ) : plans.length ? (
                    <Grid container spacing={2}>
                      {plans.map((plan) => (
                        <Grid item xs={12} md={6} key={plan?._id}>
                          <MDBox
                            p={2.5}
                            borderRadius="xl"
                            sx={{
                              border: "1px solid rgba(46, 125, 50, 0.14)",
                              background:
                                plan?.is_recommended
                                  ? "linear-gradient(135deg, rgba(28,94,67,0.12) 0%, rgba(224,186,80,0.14) 100%)"
                                  : "linear-gradient(135deg, rgba(15,23,42,0.03) 0%, rgba(25,118,210,0.04) 100%)",
                              height: "100%",
                            }}
                          >
                            <MDTypography variant="h6" fontWeight="bold">
                              {plan?.name}
                            </MDTypography>
                            <MDTypography variant="caption" color="text">
                              {plan?.code} • {plan?.billing_period || "monthly"} •{" "}
                              {plan?.duration_days || 0} days
                            </MDTypography>
                            <MDBox mt={1}>
                              <MDTypography variant="button" color="dark">
                                Rs{" "}
                                {Number(
                                  plan?.offer_price || plan?.actual_price || 0
                                ).toLocaleString("en-IN")}
                              </MDTypography>
                            </MDBox>
                            <MDTypography variant="caption" display="block" mt={1}>
                              Tests: {plan?.included_test_count || 0} • AI Credits:{" "}
                              {plan?.included_subscription_ai_credits || 0}
                            </MDTypography>
                            <MDTypography variant="caption" display="block">
                              Visible: {plan?.is_visible ? "Yes" : "No"} • Active:{" "}
                              {plan?.is_active ? "Yes" : "No"} • Version:{" "}
                              {plan?.current_version || 1}
                            </MDTypography>
                            <MDTypography variant="caption" display="block">
                              Discount: {plan?.discount_percentage || 0}% • Max/order: Rs{" "}
                              {Number(plan?.maximum_discount_per_order || 0).toLocaleString(
                                "en-IN"
                              )}
                            </MDTypography>

                            <MDBox mt={2} display="flex" gap={1} flexWrap="wrap">
                              <MDButton
                                size="small"
                                color="info"
                                onClick={() => navigate(`/subscriptions/${plan?._id}/edit`)}
                              >
                                Edit
                              </MDButton>
                              <MDButton
                                size="small"
                                color={plan?.is_active ? "warning" : "success"}
                                onClick={() =>
                                  handlePlanToggle(plan?._id, {
                                    is_active: !plan?.is_active,
                                  })
                                }
                              >
                                {plan?.is_active ? "Deactivate" : "Activate"}
                              </MDButton>
                              <MDButton
                                size="small"
                                color={plan?.is_visible ? "secondary" : "info"}
                                onClick={() =>
                                  handlePlanToggle(plan?._id, {
                                    is_visible: !plan?.is_visible,
                                  })
                                }
                              >
                                {plan?.is_visible ? "Hide" : "Show"}
                              </MDButton>
                              <MDButton
                                size="small"
                                color={plan?.is_recommended ? "dark" : "primary"}
                                onClick={() =>
                                  handlePlanToggle(plan?._id, {
                                    is_recommended: !plan?.is_recommended,
                                  })
                                }
                              >
                                {plan?.is_recommended
                                  ? "Unmark Recommended"
                                  : "Mark Recommended"}
                              </MDButton>
                              <MDButton
                                size="small"
                                color="error"
                                onClick={() => setDeleteCandidate(plan)}
                              >
                                Delete
                              </MDButton>
                            </MDBox>
                          </MDBox>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <MDTypography variant="button" color="text">
                      No subscription plans created yet.
                    </MDTypography>
                  )}
                </MDBox>
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <PurchaseOfferManager onNotify={showNotice} />
          </Grid>
        </Grid>
      </MDBox>

      <Dialog open={Boolean(deleteCandidate)} onClose={() => setDeleteCandidate(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Subscription Plan</DialogTitle>
        <DialogContent>
          <MDTypography variant="button" color="text">
            {deleteCandidate?.name
              ? `Do you want to delete "${deleteCandidate.name}"? This works only if the plan has not been used in subscriptions or payment orders yet.`
              : "Do you want to delete this subscription plan?"}
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton color="secondary" onClick={() => setDeleteCandidate(null)}>
            Cancel
          </MDButton>
          <MDButton color="error" onClick={handleDeletePlan}>
            Delete
          </MDButton>
        </DialogActions>
      </Dialog>

      <MDSnackbar
        color={notiColor}
        icon="notifications"
        title="AgriQuiz"
        content={message}
        dateTime=""
        open={notification}
        onClose={() => setNotification(false)}
        close={() => setNotification(false)}
        bgWhite
      />
    </DashboardLayout>
  );
}

export default SubscriptionManagement;
